import { shiftIsoDate } from '../../utils/date.utils';
import { notifications } from '@mantine/notifications';
import { ShiftAuthRequestError } from '../../services/auth/shift-auth.service';
import type { ShiftScheduleSlotCandidate, ShiftScheduleSection } from '../../models/schedule.types';
import { compareShiftScheduleCandidates } from 'shared-models/browser';
import type { ShiftStreamEvent } from 'shared-models';

export type CandidateSchedulingEvent = Extract<ShiftStreamEvent, { type: 'employeePriority.upserted' | 'employeeEligibility.upserted' | 'employeeLimits.upserted' }>;

export function applyCandidateSchedulingEvent(candidates: ShiftScheduleSlotCandidate[], event: CandidateSchedulingEvent, roleId: number): ShiftScheduleSlotCandidate[] {
  if (event.type === 'employeeEligibility.upserted' && event.setting.roleId !== roleId) return candidates;
  const updated = candidates.map(candidate => {
    if (candidate.employeePk !== event.setting.employeePk) return candidate;
    if (event.type === 'employeeLimits.upserted') return candidate.weeklyLimits && event.setting.revision > candidate.weeklyLimits.limits.revision
      ? { ...candidate,weeklyLimits:{...candidate.weeklyLimits,limits:event.setting} } : candidate;
    if (event.type === 'employeePriority.upserted') return event.setting.revision > candidate.priorityRevision
      ? { ...candidate, schedulingPriority: event.setting.priority, priorityRevision: event.setting.revision } : candidate;
    return event.setting.revision > candidate.eligibilityRevision
      ? { ...candidate, isRoleEligible: event.setting.isEligible, eligibilityRevision: event.setting.revision } : candidate;
  });
  return event.type === 'employeeLimits.upserted' ? updated : updated.sort(compareShiftScheduleCandidates);
}

export const WEEKDAY_SHORT_LABELS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

export const WEEKDAY_FILTER_LABELS = ["יום א'", "יום ב'", "יום ג'", "יום ד'", "יום ה'", "יום ו'", 'שבת'];

export const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const API_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

export const normalizeTimeForInput = (value: string): string =>
  API_TIME_PATTERN.test(value) ? value.slice(0, 5) : value;

export const describeCandidatePriority = (candidate: ShiftScheduleSlotCandidate): string => {
  if (candidate.isRoleEligible === false) return 'אינו מורשה לתפקיד';
  if (candidate.hasOtherSlotSameDay) {
    return 'משובץ כבר ביום זה';
  }

  if (candidate.sameSection && candidate.isAvailable) {
    return 'זמין ושייך למחלקה';
  }

  if (!candidate.sameSection && candidate.isAvailable) {
    return 'זמין במחלקה אחרת';
  }

  if (candidate.sameSection) {
    return 'לא זמין אך שייך למחלקה';
  }

  return 'לא זמין במחלקה אחרת';
};

export const describeCandidateConflict = (candidate: ShiftScheduleSlotCandidate): string | null => {
  if (!candidate.hasOtherSlotSameDay || candidate.conflicts.length === 0) {
    return null;
  }

  const firstConflict = candidate.conflicts[0];
  if (!firstConflict) {
    return null;
  }

  return `${firstConflict.startHour} - ${firstConflict.endHour} · ${firstConflict.sectionName} / ${firstConflict.shiftName} / ${firstConflict.roleName}`;
};

export const toWeekDays = (weekStartDate: string): Array<{ dateDay: string; weekdayLabel: string }> =>
  Array.from({ length: 7 }, (_, offset) => {
    const dateDay = shiftIsoDate(weekStartDate, offset);
    return {
      dateDay,
      weekdayLabel: WEEKDAY_SHORT_LABELS[offset] ?? '',
    };
  });

export const translateScheduleErrorMessage = (requestError: unknown, fallbackMessage: string): string => {
  if (!(requestError instanceof ShiftAuthRequestError)) {
    return fallbackMessage;
  }

  if (requestError.isNetworkError) {
    return 'אין תקשורת לשרת. נסו שוב בעוד רגע.';
  }

  const message = requestError.message;

  if (message.includes('Employee is not eligible for this role')) return 'העובד אינו מורשה לתפקיד זה.';

  if (message.includes('Cannot delete section with linked shifts')) {
    return 'לא ניתן למחוק מחלקה כל עוד קיימות בה משמרות.';
  }

  if (message.includes('Cannot delete shift with linked roles')) {
    return 'לא ניתן למחוק משמרת כל עוד קיימים בה תפקידים.';
  }

  if (message.includes('Cannot delete role with linked slots')) {
    return 'לא ניתן למחוק תפקיד כל עוד קיימים בו סלוטים.';
  }

  if (message.includes('Slot already exists for role and date')) {
    return 'כבר קיים סלוט עבור התפקיד ביום זה.';
  }

  if (message.includes('startHour must be earlier than endHour')) {
    return 'שעת ההתחלה חייבת להיות מוקדמת משעת הסיום.';
  }

  if (message.includes('Only manager can modify schedule')) {
    return 'רק מנהל יכול לערוך את סידור העבודה.';
  }

  if (message.includes('Employee not found in tenant scope')) {
    return 'לא ניתן לשבץ עובד שאינו שייך לעסק זה.';
  }

  return message || fallbackMessage;
};

export const showActionSuccess = (message: string) => notifications.show({ color: 'green', message });

export const showActionError = (message: string) =>
  notifications.show({ color: 'red', title: 'שגיאה', message });

export const sortSections = (sections: ShiftScheduleSection[]): ShiftScheduleSection[] =>
  [...sections]
    .sort((left, right) => {
      if (left.sortOrder !== right.sortOrder) {
        return left.sortOrder - right.sortOrder;
      }

      return left.id - right.id;
    })
    .map((section) => ({
      ...section,
      shifts: [...section.shifts]
        .sort((left, right) => {
          if (left.sortOrder !== right.sortOrder) {
            return left.sortOrder - right.sortOrder;
          }

          return left.id - right.id;
        })
        .map((shift) => ({
          ...shift,
          roles: [...shift.roles]
            .sort((left, right) => {
              if (left.sortOrder !== right.sortOrder) {
                return left.sortOrder - right.sortOrder;
              }

              return left.id - right.id;
            })
            .map((role) => ({
              ...role,
              slots: [...role.slots].sort((left, right) => {
                if (left.dateDay !== right.dateDay) {
                  return left.dateDay.localeCompare(right.dateDay);
                }

                if (left.sortOrder !== right.sortOrder) {
                  return left.sortOrder - right.sortOrder;
                }

                return left.startHour.localeCompare(right.startHour);
              }),
            })),
        })),
    }));
